import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronRight, Globe, Loader2 } from 'lucide-react';

import { useTheme } from 'components/core/theme-provider';
import { Popover, PopoverContent, PopoverTrigger } from 'components/ui/popover';
import { getRegionIcon } from 'constant/model-metadata';
import { LLM_MODEL_REGIONS } from 'constant/llm-model';
import type { IGetAgentModelResponse } from 'features/chat/agent-chat-module/services/gpt-chat.service';
import { useIsMobile } from 'hooks/use-mobile';
import { cn } from 'lib/utils';
import {
  getProviderDisplayName,
  getProviderIcon,
  getProviderIconClassName,
} from 'pages/models/models.utils';

export type ModelSelectionValue = {
  modelName: string;
  provider: string;
  isBlocksProvider: boolean;
  modelId?: string | null;
  temperature: number;
  maxTokens: number;
};

type AgentModelPickerProps = {
  isLoadingLlmModels: boolean;
  availableChatModels: IGetAgentModelResponse;
  availableCustomChatModels: IGetAgentModelResponse;
  currentLlmModel: string;
  currentLlmProvider: string;
  currentLlmModelId: string;
  isBlocksProvider: boolean;
  onModelChange: (value: ModelSelectionValue) => void;
  disabled?: boolean;
  /** Extra classes for the closed picker button, e.g. the agent settings drawer's grey field. */
  triggerClassName?: string;
};

const REGION_LOGOS: Record<string, string | undefined> = {
  eu: getRegionIcon(LLM_MODEL_REGIONS.EU),
  ch: getRegionIcon(LLM_MODEL_REGIONS.CH),
  global: undefined,
};

type BlocksModelItem = {
  modelName: string;
  modelLabel: string;
  providerId: string;
  providerLabel: string;
  defaultTemp?: number | null;
  maxTokens?: number | null;
  region?: string;
};

type CustomModelItem = {
  modelName: string;
  displayName: string;
  modelId: string;
  providerId: string;
  defaultTemp?: number | null;
  maxTokens?: number | null;
  region?: string | null;
};

const clampTemperature = (value?: number | null) => {
  const nextValue = value ?? 0.7;
  return Math.min(Math.max(nextValue, 0), 1);
};

const clampMaxTokens = (value?: number | null) => {
  const nextValue = value ?? 10000;
  return Math.min(Math.max(nextValue, 0), 10000);
};

const normalizeRegion = (region?: string | null) => region?.toLowerCase();

const RegionLogo = ({ region }: { region?: string | null }) => {
  const normalizedRegion = normalizeRegion(region);
  if (!normalizedRegion) return null;

  if (normalizedRegion === 'global') {
    return <Globe className="h-4 w-4 text-muted-foreground" />;
  }

  const icon = REGION_LOGOS[normalizedRegion];
  if (!icon) return null;

  return <img src={icon} alt={normalizedRegion.toUpperCase()} className="h-4 w-4 object-contain" />;
};

/** Picks an agent's model: Klax Models (the tenant's custom models) or Blocks Models, then the provider, then the model. */
export function AgentModelPicker({
  isLoadingLlmModels,
  availableChatModels,
  availableCustomChatModels,
  currentLlmModel,
  currentLlmProvider,
  currentLlmModelId,
  isBlocksProvider,
  onModelChange,
  disabled = false,
  triggerClassName,
}: AgentModelPickerProps) {
  const { t } = useTranslation();
  const [isModelSelectorOpen, setIsModelSelectorOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [selectedProviderGroup, setSelectedProviderGroup] = useState<string | null>(null);
  const isMobile = useIsMobile();
  const { resolvedTheme } = useTheme();

  const blocksMap = useMemo(() => {
    const map: Record<string, BlocksModelItem[]> = {};

    availableChatModels.forEach((item) => {
      const providerKey = item.provider_label;
      if (!map[providerKey]) {
        map[providerKey] = [];
      }

      map[providerKey].push({
        modelName: item.model_name,
        modelLabel: item.model_name_label,
        providerId: item.provider,
        providerLabel: item.provider_label,
        defaultTemp: item.default_temp,
        maxTokens: item.max_tokens,
        region: item.region,
      });
    });

    return map;
  }, [availableChatModels]);

  const customMap = useMemo(() => {
    const map: Record<string, CustomModelItem[]> = {};

    availableCustomChatModels.forEach((item) => {
      if (!item.id) return;
      const providerKey = item.provider_label || item.provider;
      if (!map[providerKey]) {
        map[providerKey] = [];
      }

      map[providerKey].push({
        modelName: item.model_name,
        displayName: item.model_name_label || item.model_name,
        modelId: item.id,
        providerId: item.provider,
        defaultTemp: item.default_temp,
        maxTokens: item.max_tokens,
        region: item.region,
      });
    });

    return map;
  }, [availableCustomChatModels]);

  const blockProviders = useMemo(() => Object.keys(blocksMap), [blocksMap]);
  const customProviders = useMemo(() => Object.keys(customMap), [customMap]);
  const categories = useMemo(() => {
    const nextCategories: string[] = ['custom'];

    if (blockProviders.length > 0) {
      nextCategories.push('blocks');
    }

    return nextCategories;
  }, [blockProviders.length]);

  const handleModelSelect = ({
    provider,
    modelName,
    isBlocksModel,
    modelId,
    defaultTemp,
    maxTokens,
  }: {
    provider: string;
    modelName: string;
    isBlocksModel: boolean;
    modelId?: string;
    defaultTemp?: number | null;
    maxTokens?: number | null;
  }) => {
    onModelChange({
      modelName,
      provider,
      isBlocksProvider: isBlocksModel,
      modelId: modelId || null,
      temperature: clampTemperature(defaultTemp),
      maxTokens: clampMaxTokens(maxTokens),
    });

    setIsModelSelectorOpen(false);
    setSelectedCategory(null);
    setSelectedProviderGroup(null);
  };

  let finalDisplayLabel = currentLlmModel || t('SELECT_A_MODEL') || 'Select a model...';
  let selectedRegion: string | undefined;

  if (currentLlmProvider && currentLlmModel) {
    if (isBlocksProvider) {
      outer: for (const providerLabel of blockProviders) {
        for (const model of blocksMap[providerLabel] || []) {
          if (model.providerId === currentLlmProvider && model.modelName === currentLlmModel) {
            finalDisplayLabel = `Blocks / ${model.providerLabel} / ${model.modelLabel}`;
            selectedRegion = model.region;
            break outer;
          }
        }
      }
    } else {
      const foundModel = availableCustomChatModels.find((model) => model.id === currentLlmModelId);
      const providerLabel =
        foundModel?.provider_label || getProviderDisplayName(currentLlmProvider);
      const modelLabel = foundModel?.model_name_label || currentLlmModel;

      finalDisplayLabel = `My Model / ${providerLabel} / ${modelLabel}`;
      selectedRegion = foundModel?.region ?? undefined;
    }
  }

  const currentProviderIcon = getProviderIcon(currentLlmProvider);
  const hasAnyModel = categories.length > 0;

  const renderCategories = () => (
    <div
      role="menu"
      className={cn(
        'p-2',
        isMobile ? 'w-full border-b border-border' : 'min-w-44 border-r border-border'
      )}
    >
      {categories.map((category) => {
        const label =
          category === 'custom'
            ? t('KLAX_MODELS', { defaultValue: 'Klax Models' })
            : t('BLOCKS_MODELS') || 'Blocks Models';
        const isActive = selectedCategory === category;
        const isDisabled = category === 'custom' && customProviders.length === 0;

        return (
          <button
            key={category}
            type="button"
            onClick={() => {
              if (isDisabled) return;
              setSelectedCategory(category);
              setSelectedProviderGroup(null);
            }}
            className={`flex w-full items-center justify-between rounded-md p-2 text-left text-sm transition-colors ${
              isDisabled
                ? 'cursor-not-allowed opacity-50'
                : isActive
                  ? 'bg-primary/10 text-primary dark:bg-accent dark:text-foreground'
                  : 'text-foreground hover:bg-muted hover:text-foreground'
            }`}
            disabled={isDisabled}
          >
            <span>{label}</span>
            <ChevronRight className="h-4 w-4" />
          </button>
        );
      })}
    </div>
  );

  const renderProviderPanel = () => {
    if (!selectedCategory) return null;

    const providers = selectedCategory === 'custom' ? customProviders : blockProviders;

    return (
      <div
        role="menu"
        className={cn(
          'p-2',
          isMobile ? 'w-full border-b border-border' : 'min-w-[200px] border-r border-border'
        )}
      >
        {providers.map((provider) => {
          const isActive = selectedProviderGroup === provider;

          return (
            <button
              key={provider}
              type="button"
              onClick={() => setSelectedProviderGroup(provider)}
              className={`flex w-full items-center justify-between rounded-md p-2 text-left text-sm transition-colors ${
                isActive
                  ? 'bg-primary/10 text-primary dark:bg-accent dark:text-foreground'
                  : 'text-foreground hover:bg-muted hover:text-foreground'
              }`}
            >
              <span>{provider}</span>
              <ChevronRight className="h-4 w-4" />
            </button>
          );
        })}
      </div>
    );
  };

  const renderModelsPanel = () => {
    if (!selectedCategory || !selectedProviderGroup) return null;

    if (selectedCategory === 'custom') {
      return (
        <div role="menu" className={cn('p-2', isMobile ? 'w-full' : 'min-w-56')}>
          {(customMap[selectedProviderGroup] || []).map((model) => (
            <button
              key={model.modelId}
              type="button"
              onClick={() =>
                handleModelSelect({
                  provider: model.providerId,
                  modelName: model.modelName,
                  isBlocksModel: false,
                  modelId: model.modelId,
                  defaultTemp: model.defaultTemp,
                  maxTokens: model.maxTokens,
                })
              }
              className="flex w-full items-center justify-between rounded-md p-2 text-left text-sm text-foreground transition-colors hover:bg-muted"
            >
              <div className="flex w-full items-center gap-3">
                <span className="break-words">{model.displayName}</span>
                <RegionLogo region={model.region} />
              </div>
            </button>
          ))}
        </div>
      );
    }

    return (
      <div role="menu" className={cn('p-2', isMobile ? 'w-full' : 'min-w-60')}>
        {(blocksMap[selectedProviderGroup] || []).map((model) => (
          <button
            key={`${model.providerId}-${model.modelName}`}
            type="button"
            onClick={() =>
              handleModelSelect({
                provider: model.providerId,
                modelName: model.modelName,
                isBlocksModel: true,
                defaultTemp: model.defaultTemp,
                maxTokens: model.maxTokens,
              })
            }
            className="flex w-full items-center justify-between rounded-md p-2 text-left text-sm text-foreground transition-colors hover:bg-muted"
          >
            <div className="flex w-full items-center gap-3">
              <span className="break-words">{model.modelLabel}</span>
              <RegionLogo region={model.region} />
            </div>
          </button>
        ))}
      </div>
    );
  };

  if (isLoadingLlmModels) {
    return (
      <div className="flex h-11 items-center gap-2 rounded-md border border-border bg-background px-3 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" />
        {t('LOADING_MODELS') || 'Loading models...'}
      </div>
    );
  }

  if (!hasAnyModel) {
    return (
      <div className="rounded-lg border border-dashed border-border bg-muted/20 px-4 py-3 text-sm text-muted-foreground">
        {t('NO_CHAT_MODELS_AVAILABLE') || 'No chat models available.'}
      </div>
    );
  }

  return (
    <Popover
      open={disabled ? false : isModelSelectorOpen}
      onOpenChange={(open) => {
        if (disabled) return;
        setIsModelSelectorOpen(open);
        if (!open) {
          setSelectedCategory(null);
          setSelectedProviderGroup(null);
        }
      }}
    >
      <PopoverTrigger asChild>
        <button
          type="button"
          disabled={disabled}
          className={cn(
            'flex min-h-11 w-full items-center justify-between rounded-lg border border-border bg-background px-4 py-2 text-left transition-colors hover:bg-muted/30 disabled:cursor-not-allowed disabled:opacity-50',
            triggerClassName
          )}
        >
          <div className="flex min-w-0 items-center gap-2 overflow-hidden">
            {currentProviderIcon ? (
              <img
                src={currentProviderIcon}
                alt={currentLlmProvider || t('PROVIDER') || 'Provider'}
                className={getProviderIconClassName(currentLlmProvider, resolvedTheme)}
              />
            ) : null}
            <div className="flex min-w-0 items-center gap-2 overflow-hidden">
              <span className="truncate text-sm font-medium text-foreground">
                {finalDisplayLabel}
              </span>
              <RegionLogo region={selectedRegion} />
            </div>
          </div>
          <ChevronRight
            className={cn(
              'ml-2 h-4 w-4 shrink-0 text-muted-foreground transition-transform',
              isModelSelectorOpen && 'rotate-90'
            )}
          />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        side="bottom"
        sideOffset={8}
        collisionPadding={16}
        className="w-auto max-w-[min(100vw-2rem,720px)] max-h-[min(420px,70vh)] overflow-auto rounded-md border border-border bg-background p-0 shadow-lg"
      >
        <div className={cn(isMobile ? 'flex w-full flex-col' : 'flex min-w-0')}>
          {renderCategories()}
          {renderProviderPanel()}
          {renderModelsPanel()}
        </div>
      </PopoverContent>
    </Popover>
  );
}
